import Img from "@assets/img";
import { Button } from "@components/Button";
import { getRaffleById } from "@network/Raffle";
import { RaffleResultResData, WinnersResData } from "@network/Raffle/type";
import { SCREEN } from "@type/index";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { routes } from "@const/routes";

export const RaffleResultDetail = ({ title }: { title: string }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [raffleResult, setRaffleResult] =
    useState<RaffleResultResData["data"]>();
  const [expandedDates, setExpandedDates] = useState<Set<number>>(new Set());

  const raffleId = parseInt(id as string);

  // Group winners by date
  const raffleDates = useMemo(() => {
    if (!raffleResult?.raffleDates) return [];
    return raffleResult.raffleDates;
  }, [raffleResult]);

  const toggleDate = (index: number) => {
    const newExpanded = new Set(expandedDates);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedDates(newExpanded);
  };

  useEffect(() => {
    const fetchRaffleById = async (raffleId: number) => {
      try {
        const res: RaffleResultResData = await getRaffleById(raffleId, {
          screen: SCREEN.RAFFLE_RESULT,
        });
        setRaffleResult(res.data);
        // Don't set expandedDates here since it should be indices, not the actual data
      } catch (error) {
        console.log(error);
      }
    };

    fetchRaffleById(raffleId);
  }, [raffleId]);

  return (
    <div className="w-full max-w-7xl mx-auto p-5 md:p-0">
      <Button
        className="hidden"
        icon={<img src={Img.BackButton} />}
        onClick={() => navigate(routes.raffleResult)}
      >
        Back to list
      </Button>

      {/* Desktop View */}
      <div className="hidden md:block">
        <div className="mb-6">
          <h1 className="text-[32px] font-bold text-gray-800 mb-2 flex items-center gap-2">
            {title || raffleResult?.title || "Raffle Results"}
            <svg
              width="47"
              height="52"
              viewBox="0 0 47 52"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ display: "inline-block", verticalAlign: "middle" }}
            >
              <path
                d="M27.888 2.45059C12.8782 20.9287 25.9208 38.3791 35.8568 42.0705C45.5509 25.0569 43.4957 15.6492 27.888 2.45059Z"
                fill="#52B12F"
              />
              <path
                d="M27.8877 2.45068C43.4954 15.6493 45.5505 25.057 35.8565 42.0706L27.8877 2.45068Z"
                fill="#1B5C29"
              />
              <path
                d="M1.77362 38.8172C9.80773 53.9189 25.0051 50.165 30.2266 44.6204C21.6755 33.4467 14.8739 32.1767 1.77362 38.8172Z"
                fill="#52B12F"
              />
              <path
                d="M1.77358 38.8175C14.8738 32.1769 21.6755 33.447 30.2265 44.6206L1.77358 38.8175Z"
                fill="#1B5C29"
              />
            </svg>
          </h1>
          <p className="text-base text-gray-600 m-0">
            Duration: {raffleResult?.startTime
              ? `${moment(raffleResult.startTime).format("DD MMM YYYY")} To ${moment(raffleResult.expiryDate).format("DD MMM YYYY")}`
              : ""}
          </p>
        </div>

        <div className="w-full bg-white rounded-lg overflow-hidden shadow-sm">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="bg-gray-600 text-white px-5 py-4 text-left font-semibold text-sm border-none first:rounded-tl-lg last:rounded-tr-lg">
                  Dates
                </th>
                <th className="bg-gray-600 text-white px-5 py-4 text-left font-semibold text-sm border-none">
                  Selected Number
                </th>
                <th className="bg-gray-600 text-white px-5 py-4 text-left font-semibold text-sm border-none">
                  Lucky Number
                </th>
                <th className="bg-gray-600 text-white px-5 py-4 text-left font-semibold text-sm border-none last:rounded-tr-lg">
                  Reward
                </th>
              </tr>
            </thead>
            <tbody>
              {raffleDates.map((raffle, index) => {
                const selected = raffle.selectedNumbers;
                const lucky = raffle.luckyNumber;
                const isWinner = Array.isArray(selected)
                  ? selected.map((s: any) => Number(s)).includes(Number(lucky))
                  : selected
                    ?.toString()
                    .split(/[, ]+/)
                    .map((s: string) => Number(s))
                    .some((n: number) => !Number.isNaN(n) && n === Number(lucky));

                return (
                  <tr
                    key={index}
                    className={`border-b border-gray-200 ${index % 2 === 1 ? "bg-gray-50" : "bg-white"
                      } hover:bg-blue-50`}
                  >
                    <td className="px-5 py-4 text-sm text-gray-600 border-none">
                      {raffle.date}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600 border-none">
                      {Array.isArray(raffle.selectedNumbers)
                        ? raffle.selectedNumbers.join(", ")
                        : raffle.selectedNumbers}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600 border-none">
                      {raffle.luckyNumber}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600 border-none">
                      {isWinner ? (
                        <div className="inline-flex items-center gap-2">
                          <div className="inline-flex items-center gap-1.5 bg-green-600 text-white px-3 py-1.5 rounded-md text-[13px] font-semibold">
                            🏆 You won!
                          </div>
                          <button
                            className="text-sm bg-green-600 text-white underline ml-2"
                            onClick={() => {
                              // Replace this with real claim navigation/handler
                              console.log("Claim clicked for raffle:", raffle);
                            }}
                          >
                            Click here to claim
                          </button>
                        </div>
                      ) : (
                        <div className="inline-block bg-green-50 text-green-700 px-3 py-1.5 rounded-md text-[13px] font-medium">
                          No Reward
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div >

      {/* Mobile View */}
      <div className="block md:hidden bg-[#f5f5dc] min-h-screen" >
        <div className="flex items-center gap-4 p-4 bg-white border-b border-gray-200">
          <div className="w-16 h-16 flex-shrink-0 bg-white rounded-lg flex items-center justify-content-center overflow-hidden">
            <img
              src={Img.Logo || "/placeholder-raffle.png"}
              alt="Raffle"
              className="w-full h-full object-cover"
            />
          </div>
          <h2 className="text-lg font-semibold text-gray-800 m-0 leading-snug">
            {title || raffleResult?.title || "Raffle Results"}
          </h2>
        </div>

        <div className="px-4 pb-4">
          {raffleDates.map((raffle, index) => (
            <div
              key={index}
              className="bg-white rounded-xl mt-4 overflow-hidden shadow-sm"
            >
              <div
                className="flex justify-between items-center px-5 py-4 cursor-pointer select-none border-b border-gray-100 active:bg-gray-50"
                onClick={() => toggleDate(index)}
              >
                <div className="text-base font-semibold text-gray-800">
                  {raffle.date}
                </div>
                <span
                  className={`text-xs text-gray-500 transition-transform duration-200 ${expandedDates.has(index) ? "rotate-180" : "rotate-0"
                    }`}
                >
                  ▼
                </span>
              </div>
              {expandedDates.has(index) && (
                <div className="px-5 pb-4 animate-[slideDown_0.2s_ease]">
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <div className="text-sm text-gray-500">Raffle Type</div>
                    <div className="text-sm font-semibold text-green-700 text-right">
                      Daily
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <div className="text-sm text-gray-500">Duration</div>
                    <div className="text-sm font-medium text-green-700 text-right">
                      25-8-25 to 30-8-25
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <div className="text-sm text-gray-500">Selected Number</div>
                    <div className="text-sm font-semibold text-gray-800 text-right">
                      {raffle.selectedNumbers}
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <div className="text-sm text-gray-500">Lucky Number</div>
                    <div className="text-sm font-semibold text-gray-800 text-right">
                      {raffle.luckyNumber}
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <div className="text-sm text-gray-500">Reward won</div>
                    <div className="text-sm font-semibold text-green-700 text-right">
                      {raffle.reward || "None"}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div >
  );
};
